import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { validateHSCodesWithHMRC } from '../../api/hmrcValidator'
import { getCommodityUnitValueBatch } from '../../api/comtradeValidator'
import type { HSCodeValidation } from '../../api/hmrcValidator'
import type { CommodityUnitValue } from '../../api/comtradeValidator'

export default function HSCodeSearch() {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [results, setResults] = useState<HSCodeValidation[]>([])
  const [unitValues, setUnitValues] = useState<Map<string, CommodityUnitValue>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  function copyToClipboard(code: string) {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  async function handleValidate() {
    if (!input.trim()) {
      setError('Please enter at least one HS code')
      return
    }

    setError('')
    setLoading(true)
    setResults([])

    const codes = input
      .split(/[\n,]/)
      .map(c => c.trim())
      .filter(c => c.length > 0)

    if (codes.length === 0) {
      setError('No valid HS codes found')
      setLoading(false)
      return
    }

    try {
      const validations = await validateHSCodesWithHMRC(codes)
      console.log('HMRC Validation Results:', validations)
      validations.forEach(v => {
        console.log(`HS Code ${v.hs_code}:`, {
          valid: v.valid,
          additional_codes: v.additional_codes,
          cn_additional_codes: v.cn_additional_codes,
          duty_rate: v.duty_rate,
          cn_duty_rate: v.cn_duty_rate
        })
      })
      setResults(validations)

      // Fetch unit values from UK Trade Info API
      const unitValueResults = await getCommodityUnitValueBatch(codes)
      const unitValueMap = new Map<string, CommodityUnitValue>()
      unitValueResults.forEach(uv => {
        unitValueMap.set(uv.commodity_code, uv)
        console.log(`Commodity ${uv.commodity_code}: avg unit value = ${uv.average_unit_value} ${uv.unit} (${uv.data_points} records)`)
      })
      setUnitValues(unitValueMap)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Validation failed')
    } finally {
      setLoading(false)
    }
  }

  function exportCSV() {
    if (results.length === 0) return

    const headers = ['HS Code', 'Valid', 'Official Description', 'Duty Rate (CN)', 'Additional Codes (CN)', 'Required Documents', 'Avg Unit Value (GBP/kg)', 'Data Points']
    const rows = results.map(r => {
      const additionalCodesData = (r.cn_additional_codes && String(r.cn_additional_codes).trim().length > 0)
        ? r.cn_additional_codes
        : (r.additional_codes && String(r.additional_codes).trim().length > 0)
        ? r.additional_codes
        : ''

      let additionalCodesText = ''
      if (typeof additionalCodesData === 'string') {
        additionalCodesText = additionalCodesData
      } else if (Array.isArray(additionalCodesData)) {
        additionalCodesText = additionalCodesData
          .map(ac => {
            const code = typeof ac === 'string' ? ac : (ac.code || '')
            const desc = typeof ac === 'string' ? '' : (ac.description || '')
            return desc ? `${code}: ${desc}` : code
          })
          .join('; ')
      }

      const unitValue = unitValues.get(r.hs_code)
      return [
        r.hs_code,
        r.valid ? 'Yes' : 'No',
        r.official_description || (r.error || ''),
        r.cn_duty_rate || r.duty_rate || '',
        additionalCodesText || '',
        r.required_documents?.join('; ') || '',
        unitValue?.average_unit_value ? unitValue.average_unit_value.toFixed(2) : '',
        unitValue?.data_points || ''
      ]
    })

    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hs-codes-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button
            onClick={() => navigate('/customs')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">HS Code Batch Search</span>
        </div>

        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">HS Code Batch Search</h1>
        </div>

        {/* Input Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Enter HS Codes
          </label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Paste multiple HS codes (one per line or comma-separated)
          </p>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="621790&#10;940120&#10;2208..."
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            rows={8}
          />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleValidate}
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
            >
              {loading ? 'Validating...' : 'Validate All Codes'}
            </button>
            <button
              onClick={() => {
                setInput('')
                setResults([])
                setError('')
              }}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Results ({results.length})</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {results.filter(r => r.valid).length} valid • {results.filter(r => !r.valid).length} invalid
                </p>
              </div>
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      HS Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Duty Rate
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Additional Codes (CN)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Avg Unit Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {results.map((result, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                      <td className="px-4 py-4 font-mono font-semibold text-gray-900 dark:text-white">
                        {result.hs_code}
                      </td>
                      <td className="px-4 py-4">
                        {result.valid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Valid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 max-w-xs">
                        {result.valid ? (
                          <span className="text-gray-700 dark:text-gray-300">{result.official_description || '—'}</span>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-red-600 dark:text-red-400 text-xs font-medium">{result.error || 'Unknown error'}</p>
                            {result.suggestions && result.suggestions.length > 0 && (
                              <div className="space-y-1.5">
                                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">Did you mean:</p>
                                <div className="space-y-1">
                                  {result.suggestions.map((sugg, sIdx) => (
                                    <button
                                      key={sIdx}
                                      onClick={() => {
                                        const newInput = input.replace(result.hs_code, sugg.code)
                                        setInput(newInput)
                                      }}
                                      className="block w-full text-left px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs transition-colors"
                                      title={sugg.description}
                                    >
                                      <span className="font-mono font-semibold">{sugg.code}</span>
                                      <div className="text-xs text-amber-800 dark:text-amber-200 truncate mt-0.5">{sugg.description}</div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-gray-900 dark:text-white font-medium">
                        {result.cn_duty_rate || result.duty_rate || '—'}
                      </td>
                      <td className="px-4 py-4">
                        {(() => {
                          const codesData: any = (result.cn_additional_codes && String(result.cn_additional_codes).trim().length > 0)
                            ? result.cn_additional_codes
                            : (result.additional_codes && String(result.additional_codes).trim().length > 0)
                            ? result.additional_codes
                            : null

                          if (!codesData) {
                            return <span className="text-gray-400 dark:text-gray-500">—</span>
                          }

                          const codesArray: string[] = typeof codesData === 'string'
                            ? codesData.split('; ').filter((c: string) => c.trim())
                            : Array.isArray(codesData)
                            ? codesData.map((c: any) => typeof c === 'string' ? c : c.code || '')
                            : []

                          if (codesArray.length === 0) {
                            return <span className="text-gray-400 dark:text-gray-500">—</span>
                          }

                          const displayedCodes = codesArray.slice(0, 5)
                          const hiddenCount = codesArray.length - 5

                          return (
                            <div className="flex flex-wrap gap-1.5 items-center">
                              {displayedCodes.map((code: string, cIdx: number) => (
                                <button
                                  key={cIdx}
                                  onClick={() => copyToClipboard(code.trim())}
                                  title={`Click to copy: ${code.trim()}`}
                                  className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                    copiedCode === code.trim()
                                      ? 'bg-green-200 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer'
                                  }`}
                                >
                                  {copiedCode === code.trim() ? '✓' : ''} {code.trim()}
                                </button>
                              ))}
                              {hiddenCount > 0 && (
                                <span title={`+ ${codesArray.slice(5).join(', ')}`} className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                                  +{hiddenCount}
                                </span>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-4 py-4">
                        {(() => {
                          const uv = unitValues.get(result.hs_code)
                          if (!uv) return <span className="text-gray-400">—</span>
                          if (uv.error) return <span className="text-red-600 dark:text-red-400 text-xs">{uv.error}</span>
                          return (
                            <div className="space-y-1">
                              <div className="font-mono font-medium text-gray-900 dark:text-white">
                                {uv.average_unit_value ? `£${uv.average_unit_value.toFixed(2)}/kg` : '—'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {uv.data_points} records
                              </div>
                            </div>
                          )
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && results.length === 0 && input && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              Click "Validate All Codes" to check your HS codes against the HMRC database
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
